import { joinPath } from '@eventbrite/path-utils';
import isEmpty from 'lodash/isEmpty';
import {
    BASE_URL,
    DELIVERY_METHODS_PATH,
    EXISTING_ORDER_PATH,
    GROUP_REG_JOIN_PATH,
    GROUP_REG_TYPE_SELECTION_PATH,
    LOGIN_PATH,
    ONLY_CHECKOUT_PATH,
    ORDER_SUCCESS_ANIMATION_PATH,
    PAY_IN_PATH,
    PICK_A_SEAT_PATH,
    PRE_ORDER_CONFIRMATION_ORGANIZER_QUESTIONS_PATH,
    PRE_TICKETS_ORGANIZER_QUESTIONS_PATH,
    PROTECTED_PATH,
    RESERVED_TICKETS_CONFIRMATION_PATH,
    RESERVED_TICKETS_PATH,
    SERIES_PATH,
    STATUS_PATH,
    TICKETS_COMPACT_SIZE_PATH,
    TICKETS_FULL_SIZE_PATH,
    TICKETS_WITH_CAPACITY_WAITLIST_PATH,
    TICKETS_WITH_TICKET_RESELLER_PATH,
    UNLOCK_TICKETS_PATH,
    WAITING_ROOM_PATH,
} from '../constants';
import { ConfirmStatus } from '../constants/confirmStatus';
import { EventStatus } from '../constants/eventStatus';
import { ERROR_TO_STATUS_PATH_MAPPING } from '../containers/status/constants';
import { ParsedError } from '../models/error';
import {
    getIsTicketSelectionCompactSizeFlow,
    isMyOrderFlow,
    isPayinsFlow,
} from '../selectors/app';
import { getIsOnlyCheckoutFlow } from '../selectors/app/getIsOnlyCheckoutFlow';
import { getIsEventProtected } from '../selectors/event';
import { isSeriesInfiniteCalendar } from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { getGroupRegistrationTickets } from '../selectors/tickets';
import { CheckoutState } from '../state';
import { EventNamespace } from '../state/event';
import { WaitingRoomNamespace } from '../state/waitingRoom';
import { getStatusForEvent } from './status';
import {
    shouldShowWaitlistDisabledStatusPage,
    shouldShowWaitlistInvalidStatusPage,
} from './waitlist';

export const getPath = ({
    isSeriesInfiniteCalendarViewActive,
    segments,
}: {
    isSeriesInfiniteCalendarViewActive: boolean;
    segments: string[];
}) => {
    return isSeriesInfiniteCalendarViewActive
        ? joinPath(BASE_URL, SERIES_PATH, ...segments)
        : joinPath(BASE_URL, ...segments);
};

const _shouldShowWaitingRoom = (
    event: EventNamespace,
    tickets: { ticketsById: Record<string, any> },
    waitingRoom: WaitingRoomNamespace,
): boolean => {
    if (!event) {
        return false;
    }
    return (
        waitingRoom.isActivatedForEvent &&
        !waitingRoom.isLive &&
        getStatusForEvent(event, tickets) !== EventStatus.soldOut
    );
};

/**
 * Returns the correct path to group group registration:
 * If we have individual tickets => Groupt type selection
 * Else Group join page
 * @param {Object} state the application's current state
 */
const _getGroupRegistrationPath = (state: CheckoutState) => {
    const {
        event: { groupSettings = { hasIndividualTicket: false } },
        groupRegistration = {},
    } = state;

    if (isEmpty(groupRegistration)) {
        const path = groupSettings.hasIndividualTicket
            ? GROUP_REG_TYPE_SELECTION_PATH
            : GROUP_REG_JOIN_PATH;

        return joinPath(BASE_URL, path);
    }

    // we first need to check if we have tickets for this group type
    const tickets = getGroupRegistrationTickets(state);

    if (tickets.length > 0) {
        return joinPath(BASE_URL, TICKETS_FULL_SIZE_PATH);
    }

    return joinPath(BASE_URL, UNLOCK_TICKETS_PATH);
};

export const getTicketsSelectionPagePath = (state: CheckoutState) => {
    const {
        waitingRoom = { isActivatedForEvent: false, isLive: false },
        event: { isSeriesParent, isReservedSeating, hasAdvancedTeamsEnabled },
        event,
    } = state;

    const isSeriesInfiniteCalendarViewActive = isSeriesInfiniteCalendar(state);

    if (_shouldShowWaitingRoom(event, state.tickets, waitingRoom)) {
        return joinPath(BASE_URL, WAITING_ROOM_PATH);
    }

    if (isSeriesParent) {
        return joinPath(BASE_URL, SERIES_PATH);
    }

    if (isReservedSeating) {
        return getPath({
            isSeriesInfiniteCalendarViewActive,
            segments: [RESERVED_TICKETS_PATH],
        });
    }

    const eventStatus = getStatusForEvent(state.event, state.tickets);

    if (
        isSeriesInfiniteCalendarViewActive &&
        eventStatus === EventStatus.soldOutWithCapacityWaitlist
    ) {
        return joinPath(
            BASE_URL,
            SERIES_PATH,
            TICKETS_WITH_CAPACITY_WAITLIST_PATH,
        );
    }

    /**
     * Only route to group join and registration selections pages
     * when team settings are enabled, and you are not coming from ticket selection page
     * This enables us to stay on ticket selection page in case of error after having chosen the group reg type.
     */
    if (hasAdvancedTeamsEnabled) {
        return _getGroupRegistrationPath(state);
    }

    if (getIsTicketSelectionCompactSizeFlow(state)) {
        return joinPath(BASE_URL, TICKETS_COMPACT_SIZE_PATH);
    }

    if (getIsOnlyCheckoutFlow(state)) {
        return joinPath(BASE_URL, ONLY_CHECKOUT_PATH);
    }

    return getPath({
        isSeriesInfiniteCalendarViewActive,
        segments: [TICKETS_FULL_SIZE_PATH],
    });
};

/**
 * Additional 'or' conditions can be added here in the case of
 * other errors being returned from the backend
 * @param {string} eventStatus
 * @param {array} errors
 * @return {Boolean}
 */
const _shouldShowErrorStatusPage = (
    eventStatus: EventStatus,
    errors?: ParsedError[],
) =>
    shouldShowWaitlistInvalidStatusPage(eventStatus, errors) ||
    shouldShowWaitlistDisabledStatusPage(errors);

/**
 * Returns the correct initial path to be routed to on app load
 * @param {object} state    the full state tree
 */
export const getInitialPath = (state: CheckoutState) => {
    const {
        app: { errors, waitlistCode, existingOrderId },
        event,
        tickets,
        waitingRoom = { isActivatedForEvent: false, isLive: false },
    } = state;
    const isSeriesInfiniteCalendarViewActive = isSeriesInfiniteCalendar(state);

    if (_shouldShowWaitingRoom(event, tickets, waitingRoom)) {
        return joinPath(BASE_URL, WAITING_ROOM_PATH);
    }

    // Protected events surpasses all conditions:
    // If the event is proteced, we should always show FIRST the protected banner.
    if (getIsEventProtected(state)) {
        return joinPath(BASE_URL, PROTECTED_PATH);
    }

    if (isMyOrderFlow(state)) {
        return joinPath(BASE_URL, PRE_TICKETS_ORGANIZER_QUESTIONS_PATH);
    }

    // If we have an order ID, we consider the order has been created manually previously
    // Therefore: no waiting room, no event series parent, no event statuses (no sold out, sales ended, unlock...)
    if (existingOrderId) {
        return joinPath(BASE_URL, EXISTING_ORDER_PATH);
    }

    // Trigger the PayIn Flow
    if (isPayinsFlow(state)) {
        return joinPath(BASE_URL, PAY_IN_PATH);
    }

    let initialPath = getTicketsSelectionPagePath(state);

    if (isSeriesInfiniteCalendarViewActive) {
        return initialPath;
    }

    const eventStatus = getStatusForEvent(event, tickets, waitlistCode);

    if (eventStatus) {
        if (errors?.length && _shouldShowErrorStatusPage(eventStatus, errors)) {
            const error = errors[0].error;
            const path = ERROR_TO_STATUS_PATH_MAPPING[error];

            initialPath = joinPath(BASE_URL, STATUS_PATH, path);
        } else if (eventStatus === EventStatus.allTicketsRequireUnlock) {
            // In case we have group settings enabled, the group selection is always shown first before the access code page.
            const path = event.hasAdvancedTeamsEnabled
                ? _getGroupRegistrationPath(state)
                : UNLOCK_TICKETS_PATH;

            initialPath = joinPath(BASE_URL, path);
        } else if (eventStatus === EventStatus.soldOutWithCapacityWaitlist) {
            initialPath = joinPath(
                BASE_URL,
                TICKETS_WITH_CAPACITY_WAITLIST_PATH,
            );
        } else if (eventStatus === EventStatus.soldOutWithTicketReseller) {
            initialPath = joinPath(BASE_URL, TICKETS_WITH_TICKET_RESELLER_PATH);
        } else {
            initialPath = joinPath(BASE_URL, STATUS_PATH, eventStatus);
        }
    }
    return initialPath;
};

export const getOrganizerQuestionsPage = () =>
    joinPath(BASE_URL, PRE_ORDER_CONFIRMATION_ORGANIZER_QUESTIONS_PATH);

export const getOrderSuccessAnimationPage = () =>
    joinPath(BASE_URL, ORDER_SUCCESS_ANIMATION_PATH);

export const AbandonOrderCloseAttemptParams: Record<
    string,
    { statusPath: String; pathName: string }
> = {
    [RESERVED_TICKETS_CONFIRMATION_PATH]: {
        statusPath: ConfirmStatus.closeForReservedTicketConfirmation,
        pathName: RESERVED_TICKETS_CONFIRMATION_PATH,
    },
    [PICK_A_SEAT_PATH]: {
        statusPath: ConfirmStatus.closeForPickASeat,
        pathName: PICK_A_SEAT_PATH,
    },
    [DELIVERY_METHODS_PATH]: {
        statusPath: ConfirmStatus.closeToDeliveryMethod,
        pathName: DELIVERY_METHODS_PATH,
    },
    [LOGIN_PATH]: {
        statusPath: ConfirmStatus.closeForLogin,
        pathName: LOGIN_PATH,
    },
};
